import type React from "react";

import { faReceipt } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Stack, Typography } from "@mui/material";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import invariant from "ts-invariant";

import { useGetBillingPortalUrl } from "api/services/admin/hooks";
import { useProfileContext } from "contexts/ProfileContext";
import { BaseButton } from "shared/atoms/BaseButton";
import { WidgetBox } from "shared/containers/WidgetBox";
import { convertCentsToDollarsNumber } from "utils/convertCentsToDollars";
import { translateCurrency } from "utils/translations/translateCurrency";

interface Props {
  professionalsCount?: number;
  hcoID: number;
}

export const Subscription: React.FC<Props> = ({ hcoID, professionalsCount }) => {
  const { t } = useTranslation();
  const { profile } = useProfileContext();

  invariant(profile, "Profile must be defined");

  const { data: billingPortalData } = useGetBillingPortalUrl({
    health_care_organization_id: hcoID,
  });

  const hco = profile.health_care_organization;
  const stripeMetadata = profile.health_care_organization?.stripe_metadata;

  invariant(stripeMetadata, "Stripe metadata must be defined");

  const portalUrl = billingPortalData?.billing_portal_url;

  const renewalDate = stripeMetadata.billing_period_end
    ? format(new Date(stripeMetadata.billing_period_end), "MMM d, yyyy")
    : null;

  const billingAmount =
    stripeMetadata.total_cents != null && stripeMetadata.currency
      ? translateCurrency(convertCentsToDollarsNumber(stripeMetadata.total_cents), stripeMetadata.currency)
      : null;

  return (
    <WidgetBox
      title={t("admin.subscription.title")}
      icon={<FontAwesomeIcon icon={faReceipt} />}
      color="blue"
      skipPadding
      toolbar={
        <BaseButton
          variant="action"
          text={t("admin.subscription.manage_subscription")}
          disabled={!portalUrl}
          onClick={() => portalUrl && window.open(portalUrl, "_blank")}
        />
      }
    >
      <Stack direction="row" gap={3} flexWrap="wrap">
        <SubscriptionCard>
          <CardContent
            label={t("admin.subscription.seats")}
            value={
              hco?.seats
                ? t("admin.subscription.seats_used", {
                    used: professionalsCount ?? 0,
                    total: hco.seats,
                  })
                : "-"
            }
          />
        </SubscriptionCard>
        <SubscriptionCard>
          <CardContent
            label={t("admin.subscription.price")}
            value={
              hco?.platform_fee_cents
                ? `${translateCurrency(
                    convertCentsToDollarsNumber(hco.platform_fee_cents),
                    stripeMetadata.currency ?? undefined
                  )}${t("admin.subscription.price_per_seat")}`
                : "-"
            }
          />
        </SubscriptionCard>
        <SubscriptionCard>
          <CardContent
            label={t("admin.subscription.next_billing")}
            value={[renewalDate, billingAmount].filter(Boolean).join(" - ") || "-"}
          />
        </SubscriptionCard>
        <SubscriptionCard>
          <CardContent
            label={t("admin.subscription.payment_method")}
            value={
              stripeMetadata.payment_method === "card" && stripeMetadata.card_last4
                ? t("admin.subscription.card_ending_in", { last4: stripeMetadata.card_last4 })
                : stripeMetadata.payment_method || "-"
            }
          />
        </SubscriptionCard>
      </Stack>
    </WidgetBox>
  );
};

const CardContent: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const theme = useTheme();
  return (
    <Stack direction="column" gap={0.5}>
      <Typography variant="subtitle1" sx={{ lineHeight: "16px", color: theme.colors.redesign.db60 }}>
        {label}
      </Typography>
      <Typography variant="body_bold" sx={{ lineHeight: "24px" }}>
        {value}
      </Typography>
    </Stack>
  );
};

const SubscriptionCard = styled.div`
  border: 1px solid ${props => props.theme.colors.redesign.db20};
  border-radius: 12px;
  padding: 16px;
  flex: 1 1 150px;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  ${({ theme }) => theme.belowMobileBreakpoint} {
    flex-direction: column;
  }
`;
